import {
    applyApplyCopilotActions,
    applyConvertSubjectsToNewPart,
    applyCreateLocalPart,
    applyDeleteSubDocumentData,
    applyMergeSubDocumentData,
    applyReplaceElementPartVersionDataCache,
    applySetAssetUserData,
    applySetBelongsToPartUid,
    applySetControlUserData,
    applySetElementUserData,
    applySetRulesetUserData,
    applyStartReceivingLatestDraftsForPartUid,
    applyStopReceivingLatestDraftsForPartUid,
    applyUpdatePlotsPosition,
    applyUpdatePropertyVisibility,
    applyUpdateRouteProperties,
    applyUpdateRoutesLabel,
    applyUpdateWiring,
} from "../actions/miscActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type MiscSliceState = Pick<
    DocumentStoreState,
    | "setBelongsToPartUid"
    | "setRulesetUserData"
    | "setElementUserData"
    | "setAssetUserData"
    | "setControlUserData"
    | "updateRouteProperties"
    | "updateRoutesLabel"
    | "updateWiring"
    | "convertSubjectsToNewPart"
    | "createLocalPart"
    | "replaceElementPartVersionDataCache"
    | "mergeSubDocumentData"
    | "deleteSubDocumentData"
    | "startReceivingLatestDraftsForPartUid"
    | "stopReceivingLatestDraftsForPartUid"
    | "updatePlotsPosition"
    | "updatePropertyVisibility"
    | "applyCopilotActions"
    | "createProjectDoc"
    | "deleteProjectDoc"
    | "updateProjectDoc"
>;

export function createMiscSlice(deps: SliceDependencies): MiscSliceState {
    const {primitives, get} = deps;

    return {
        setBelongsToPartUid: (partUid) => {
            performDocumentMutation(primitives, (draft) => applySetBelongsToPartUid(draft, partUid), {
                actionType: "setBelongsToPartUid",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        setRulesetUserData: (rulesetUid, userData) => {
            performDocumentMutation(primitives, (draft) => applySetRulesetUserData(draft, rulesetUid, userData), {
                actionType: "setRulesetUserData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setElementUserData: (elementUid, userData) => {
            performDocumentMutation(primitives, (draft) => applySetElementUserData(draft, elementUid, userData), {
                actionType: "setElementUserData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setAssetUserData: (assetUid, userData) => {
            performDocumentMutation(primitives, (draft) => applySetAssetUserData(draft, assetUid, userData), {
                actionType: "setAssetUserData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setControlUserData: (controlUid, userData) => {
            performDocumentMutation(primitives, (draft) => applySetControlUserData(draft, controlUid, userData), {
                actionType: "setControlUserData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        updateRouteProperties: (routeUid, properties) => {
            performDocumentMutation(primitives, (draft) => applyUpdateRouteProperties(draft, routeUid, properties), {
                actionType: "updateRouteProperties",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        updateRoutesLabel: (routes) => {
            performDocumentMutation(primitives, (draft) => applyUpdateRoutesLabel(draft, routes), {
                actionType: "updateRoutesLabel",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        updateWiring: (vertexUpdates, selectedObjectUids) => {
            performDocumentMutation(
                primitives,
                (draft) => applyUpdateWiring(draft, vertexUpdates, selectedObjectUids),
                {
                    actionType: "updateWiring",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        convertSubjectsToNewPart: (args) => {
            performDocumentMutation(
                primitives,
                (draft) => applyConvertSubjectsToNewPart(draft, args.elements, args.newElement, args.vertexUpdates),
                {
                    actionType: "convertSubjectsToNewPart",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        createLocalPart: (args) => {
            performDocumentMutation(primitives, (draft) => applyCreateLocalPart(draft, args.element, args.newElement), {
                actionType: "createLocalPart",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        replaceElementPartVersionDataCache: (elements) => {
            performDocumentMutation(primitives, (draft) => applyReplaceElementPartVersionDataCache(draft, elements), {
                actionType: "replaceElementPartVersionDataCache",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        mergeSubDocumentData: (subDocumentDatas) => {
            performDocumentMutation(primitives, (draft) => applyMergeSubDocumentData(draft, subDocumentDatas), {
                actionType: "mergeSubDocumentData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        deleteSubDocumentData: (elementUids) => {
            performDocumentMutation(primitives, (draft) => applyDeleteSubDocumentData(draft, elementUids), {
                actionType: "deleteSubDocumentData",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        startReceivingLatestDraftsForPartUid: (partUid) => {
            const document = get().document;
            if (!document) return;
            const elementDataForPart = Object.values(document.elements).find(
                (element) => element.part_version_data_cache.part_uid === partUid,
            );
            if (!elementDataForPart) return;
            const currentPartVersion = elementDataForPart.part_version_data_cache.version;
            performDocumentMutation(
                primitives,
                (draft) => applyStartReceivingLatestDraftsForPartUid(draft, partUid, currentPartVersion),
                {
                    actionType: "startReceivingLatestDraftsForPartUid",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        stopReceivingLatestDraftsForPartUid: (partUid) => {
            performDocumentMutation(primitives, (draft) => applyStopReceivingLatestDraftsForPartUid(draft, partUid), {
                actionType: "stopReceivingLatestDraftsForPartUid",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        updatePlotsPosition: (elementId, position, attached) => {
            performDocumentMutation(
                primitives,
                (draft) => applyUpdatePlotsPosition(draft, elementId, position, attached),
                {
                    actionType: "updatePlotsPosition",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        updatePropertyVisibility: (elementIds, propertyId, visibility) => {
            performDocumentMutation(
                primitives,
                (draft) => applyUpdatePropertyVisibility(draft, elementIds, propertyId, visibility),
                {
                    actionType: "updatePropertyVisibility",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        applyCopilotActions: (args) => {
            performDocumentMutation(
                primitives,
                (draft) =>
                    applyApplyCopilotActions(
                        draft,
                        args.actions,
                        args.portalPart,
                        args.groundPart,
                        args.userUid,
                        args.resultCallback,
                    ),
                {
                    actionType: "applyCopilotActions",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        createProjectDoc: (projectDocUid, name, content = "") => {
            primitives.notifyServices({
                type: "createProjectDoc",
                currentUser: primitives.getCurrentUser(),
                payload: {
                    projectDocUid,
                    name,
                    content,
                    canUndo: true,
                    updatesDocumentTimestamp: true,
                    shouldGenerateActionRecord: true,
                },
            });
        },

        deleteProjectDoc: (projectDocUid, name, content = "") => {
            primitives.notifyServices({
                type: "deleteProjectDoc",
                currentUser: primitives.getCurrentUser(),
                payload: {
                    projectDocUid,
                    name,
                    content,
                    canUndo: true,
                    updatesDocumentTimestamp: true,
                    shouldGenerateActionRecord: true,
                },
            });
        },

        updateProjectDoc: (projectDocUid, name, content, previousContent) => {
            primitives.notifyServices({
                type: "updateProjectDoc",
                currentUser: primitives.getCurrentUser(),
                payload: {
                    projectDocUid,
                    name,
                    content,
                    previousContent,
                    canUndo: true,
                    updatesDocumentTimestamp: true,
                    shouldGenerateActionRecord: true,
                },
            });
        },
    };
}
